class HashQuery {
    coords: [number, number];
    zoom: number;
    queryStringObject: Record<string, string | number | boolean>;
    static instance;

    constructor() {
        if (!HashQuery.instance) {
            HashQuery.instance = this;

            this.pullMapPosition();
            this.pullQuery();
        }

        return HashQuery.instance;
    }

    // Sanitize keys to prevent prototype pollution
    private sanitizeKey(key: string): string {
        return '$' + key;
    }

    // Unsanitize keys for external use
    private unsanitizeKey(key: string): string {
        return key.startsWith('$') ? key.substring(1) : key;
    }

    getPosition() {
        this.pullMapPosition();
        return this.coords;
    }

    getZoom() {
        this.pullMapPosition();
        return this.zoom;
    }

    getQueryAsObject() {
        this.pullQuery();
        // Return a new object with unsanitized keys
        const result: Record<string, string | number | boolean> = {};
        for (const key in this.queryStringObject) {
            if (Object.hasOwn(this.queryStringObject, key)) {
                const cleanKey = key.startsWith('$') ? this.unsanitizeKey(key) : key;
                result[cleanKey] = this.queryStringObject[key];
            }
        }
        return result;
    }

    getQueryByKey(key: string): unknown {
        this.pullQuery();
        const sanitized = this.sanitizeKey(key);
        // Try sanitized key first, fallback to unsanitized for backward compatibility
        return (
            this.queryStringObject &&
            (this.queryStringObject[sanitized] !== undefined ? this.queryStringObject[sanitized] : this.queryStringObject[key])
        );
    }

    hasQueryKey(key: string): boolean {
        this.pullQuery();
        // Check sanitized key first, fallback to unsanitized for backward compatibility
        return this.queryStringObject && (Object.hasOwn(this.queryStringObject, this.sanitizeKey(key)) || Object.hasOwn(this.queryStringObject, key));
    }

    setMapPosition(coords: [number, number], zoom: number, addHistory?: boolean): void {
        this.pullQuery();
        this.coords = coords;
        this.zoom = zoom;
        this.updateHash(addHistory);
    }

    setQueryValue(key: string, value: string | number | boolean, addHistory: boolean) {
        this.pullQuery();
        this.queryStringObject[this.sanitizeKey(key)] = value;
        this.updateHash(addHistory);
    }

    removeQueryKey(key: string, addHistory: boolean) {
        this.pullQuery();
        delete this.queryStringObject[this.sanitizeKey(key)];
        this.updateHash(addHistory);
    }

    private pullMapPosition() {
        const mapPos = window && window.location.hash.split('?')[0].split('#')[1];
        if (!mapPos) {
            return;
        }
        const posArray = mapPos.split(',').map(Number);
        if (
            posArray.length < 2 ||
            !posArray.slice(0, 3).reduce((prev, curr) => {
                return prev && !isNaN(curr);
            }, true)
        ) {
            return;
        }

        this.coords = posArray.slice(0, 2) as [number, number];
        this.zoom = posArray[2];
    }

    private pullQuery(): Record<string, string | number | boolean> {
        const hash = window && window.location.hash.split('#')[1];
        let queryString;
        if (!hash) {
            this.queryStringObject = {};
            return;
        }
        if (hash.indexOf('?') === -1) {
            queryString = /^[^\d]/.test(hash) ? hash : null;
        } else {
            queryString = hash.split('?')[1];
        }

        if (!queryString) {
            this.queryStringObject = {};
            return;
        }

        this.queryStringObject = queryString.split('&').reduce((params, param) => {
            const [key, value] = param.split('=');
            params[this.sanitizeKey(key)] = value ? decodeURIComponent(value) : '';
            return params;
        }, {});
    }

    private updateHash(addHistory) {
        const newMapPosition = this.coords && this.zoom && this.coords.concat([this.zoom]).join(',');
        const newQueryString =
            this.queryStringObject &&
            Object.keys(this.queryStringObject)
                .map((k) => {
                    const unsanitizedKey = this.unsanitizeKey(k);
                    return (
                        `${encodeURIComponent(unsanitizedKey)}` +
                        (this.queryStringObject[k] ? `=${encodeURIComponent(this.queryStringObject[k])}` : '')
                    );
                })
                .join('&');
        const newHash =
            newMapPosition || newQueryString
                ? '#' + (newMapPosition || '') + (newMapPosition && newQueryString ? '?' : '') + (newQueryString || '')
                : window.location.pathname + window.location.search;
        window.history[addHistory ? 'pushState' : 'replaceState'](null, null, newHash);
    }
}

const instance = new HashQuery();

export default instance;
